import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  useDisclosure,
  Button,
  Text,
  useColorModeValue,
  Heading,
  ModalHeader,
  ModalCloseButton,
  Stack,
  HStack,
  VStack,
  Box,
  Divider,
  useClipboard,
  IconButton,
  Tooltip,
  Icon,
  Select,
  FormControl,
  FormLabel,
  SimpleGrid
} from "@chakra-ui/react";
import { MdContentCopy, MdCheck } from "react-icons/md";
import { COLORS } from "../../../constants/colors";
import { useContext, useEffect, useState, useCallback } from "react";
import { UserProfileContext } from "../../../context/userProfileContext";
import { AuthContext } from "../../../context/authContext";
import { useNavigate } from "react-router-dom";
import { PROTECTED_PATHS } from "../../../app/constants";
import {
  getEstateDue,
  getEstateInfo,
  initializePayment,
  verifyEstateDuePayment
} from "../../estateDue/service";
import {
  infoNotifier,
  errorNotifier,
  successNotifier
} from "../../../components/NotificationHandler";
import axiosInstance from "../../../services/api";

export const ConfirmPaymentModal = ({ dueTypes, totalPendingAmount }) => {
  const bgColor = useColorModeValue(COLORS.primary, COLORS.green);
  const cardBg = useColorModeValue(COLORS.lightGrey, COLORS.white);
  const btnBg = useColorModeValue(COLORS.lightGrey, COLORS.green);
  const btnColor = useColorModeValue(COLORS.green, COLORS.white);
  const [isLoading, setIsLoading] = useState(false);
  const { userProfile } = useContext(UserProfileContext);
  const { tenantName, email } = userProfile;
  const { userInfo } = useContext(AuthContext);
  const { userId } = userInfo;
  const [estateDue, setEstateDue] = useState([]);
  const [estateInfo, setEstateInfo] = useState([]);
  const [monthsAhead, setMonthsAhead] = useState(1);
  const [paymentData, setPaymentData] = useState({});
  const [paymentModal, setPaymentModal] = useState(false);
  const [transactionCharge, setTransactionCharge] = useState(0);
  const navigate = useNavigate();
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");

  // Virtual Account States
  const [virtualAccountData, setVirtualAccountData] = useState(null);
  const [virtualAccountModal, setVirtualAccountModal] = useState(false);
  const [virtualAccountLoading, setVirtualAccountLoading] = useState(false);

  const { MY_WALLET } = PROTECTED_PATHS;

  useEffect(() => {
    const fixedCharge = 100;
    const charges = 0.015 * dueTypes?.amount;
    if (dueTypes?.amount < 2000) {
      setTransactionCharge(charges);
      return;
    } else if (charges >= 2000) {
      setTransactionCharge(2000 + fixedCharge);
      return;
    } else {
      setTransactionCharge(charges + fixedCharge);
    }
  }, [dueTypes?.amount]);

  useEffect(() => {
    getEstateDue(userId, setIsLoading, setEstateDue);
    getEstateInfo(userId, setIsLoading, setEstateInfo);
  }, [userId]);

  const payEstateDue = () => {
    const payload = {
      amount: dueTypes?.amount + transactionCharge + totalPendingAmount,
      tenantName,
      email,
      userId,
      duration: monthsAhead,
      dueTypeId: dueTypes?._id,
      month: selectedMonth,
      year: selectedYear,
      totalPendingAmount: totalPendingAmount
    };

    console.log(payload, "payload");
    console.log(dueTypes, "duetypess");

    if (!payload.email) {
      infoNotifier("please set your profile and add your Email first ");
      return;
    }

    initializePayment(payload, setIsLoading, setPaymentData, setPaymentModal);
    setPaymentModal(true);
  };

  const handlePayment = async () => {
    try {
      setPaymentLoading(true);
      const res = await axiosInstance.post("/route-pay/estate-due", {
        amount: dueTypes?.amount + transactionCharge,
        tenantName,
        email,
        userId,
        duration: monthsAhead,
        dueTypeId: dueTypes?._id
      });

      const paymentInfo = res?.data;
      setPaymentData(paymentInfo);

      const tnxRef = paymentInfo?.data?.transactionReference;
      if (!tnxRef) {
        throw new Error("Transaction reference missing.");
      }

      localStorage.setItem("Due-tnx", tnxRef);
      console.log("Transaction reference stored:", tnxRef);

      const redirectUrl = paymentInfo?.data?.redirectUrl;
      if (redirectUrl) {
        console.log("Redirecting to:", redirectUrl);
        window.location.href = redirectUrl;
      } else {
        console.warn("Redirect URL is missing.");
      }
    } catch (error) {
      console.error("Payment error:", error);
      errorNotifier("Payment failed. Please try again.");
      throw error;
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleVirtualAccountPayment = async () => {
    try {
      setVirtualAccountLoading(true);

      if (!email) {
        infoNotifier("Please set your profile and add your Email first");
        return;
      }

      const payload = {
        amount: dueTypes?.amount + transactionCharge + totalPendingAmount,
        tenantName,
        email,
        userId,
        duration: monthsAhead,
        dueTypeId: dueTypes?._id,
        month: selectedMonth,
        year: selectedYear,
        totalPendingAmount: totalPendingAmount
      };

      const res = await axiosInstance.post(
        "/virtual-account/pay-dues",
        payload
      );

      if (res?.data) {
        setVirtualAccountData(res.data);
        setVirtualAccountModal(true);
        successNotifier("Virtual account details generated successfully");
      }
    } catch (error) {
      console.error("Virtual account error:", error);
      errorNotifier(
        error?.response?.data?.message ||
          "Failed to generate virtual account. Please try again."
      );
    } finally {
      setVirtualAccountLoading(false);
    }
  };

  const verifyPayments = () => {
    const payload = {
      userId,
      reference: paymentData?.reference,
      type: "paystack"
    };

    verifyEstateDuePayment(payload, navigate);
  };

  const { isOpen, onOpen, onClose } = useDisclosure();
  const paymentChannel = "virtual-account"; // paystack or routepay or virtual-account or both

  // Virtual Account Modal Component
  const VirtualAccountDetailsModal = () => {
    // Extract data from the new response structure
    const accountNumber =
      virtualAccountData?.result?.data?.customer?.account?.number;
    const bankName = virtualAccountData?.result?.data?.customer?.account?.bank;
    const accountName =
      virtualAccountData?.result?.data?.customer?.account?.name;
    const reference = virtualAccountData?.result?.data?.transaction?.reference;
    const amount = virtualAccountData?.result?.data?.order?.amount;

    const { hasCopied: hasCopiedAccount, onCopy: onCopyAccount } = useClipboard(
      accountNumber || ""
    );
    const { hasCopied: hasCopiedRef, onCopy: onCopyRef } = useClipboard(
      reference || ""
    );

    return (
      <Modal
        isOpen={virtualAccountModal}
        onClose={() => setVirtualAccountModal(false)}
        isCentered
        size="sm"
        trapFocus={false}
      >
        <ModalOverlay />
        <ModalContent bg="white" borderRadius="lg">
          <ModalHeader color={COLORS.green} borderBottom="1px solid #e2e8f0">
            Transfer Payment Details
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={4}>
            <VStack spacing={3} align="stretch">
              <Box bg={COLORS.lightGrey} p={3} borderRadius="md">
                <Text fontSize="sm" color="gray.600" mb={1}>
                  Bank Name
                </Text>
                <Text fontSize="md" fontWeight="600">
                  {bankName}
                </Text>
              </Box>

              <Box bg={COLORS.lightGrey} p={3} borderRadius="md">
                <HStack justify="space-between">
                  <Box flex={1}>
                    <Text fontSize="sm" color="gray.600" mb={1}>
                      Account Number
                    </Text>
                    <Text fontSize="md" fontWeight="600">
                      {accountNumber}
                    </Text>
                  </Box>
                  <Tooltip label={hasCopiedAccount ? "Copied!" : "Copy"}>
                    <IconButton
                      icon={hasCopiedAccount ? <MdCheck /> : <MdContentCopy />}
                      onClick={onCopyAccount}
                      colorScheme={hasCopiedAccount ? "green" : "gray"}
                      variant="ghost"
                      size="sm"
                      aria-label="Copy account number"
                    />
                  </Tooltip>
                </HStack>
              </Box>

              <Box bg={COLORS.lightGrey} p={3} borderRadius="md">
                <Text fontSize="sm" color="gray.600" mb={1}>
                  Account Name
                </Text>
                <Text fontSize="md" fontWeight="600">
                  {accountName}
                </Text>
              </Box>

              <Divider />

              <Box bg={COLORS.lightGrey} p={3} borderRadius="md">
                <Text fontSize="sm" color="gray.600" mb={1}>
                  Amount to Pay
                </Text>
                <Text fontSize="xl" fontWeight="700" color={COLORS.green}>
                  &#8358; {amount?.toLocaleString()}
                </Text>
              </Box>

              {reference && (
                <Box bg={COLORS.lightGrey} p={3} borderRadius="md">
                  <HStack justify="space-between">
                    <Box flex={1}>
                      <Text fontSize="sm" color="gray.600" mb={1}>
                        Reference
                      </Text>
                      <Text fontSize="sm" fontWeight="500" noOfLines={1}>
                        {reference}
                      </Text>
                    </Box>
                    <Tooltip label={hasCopiedRef ? "Copied!" : "Copy"}>
                      <IconButton
                        icon={hasCopiedRef ? <MdCheck /> : <MdContentCopy />}
                        onClick={onCopyRef}
                        colorScheme={hasCopiedRef ? "green" : "gray"}
                        variant="ghost"
                        size="sm"
                        aria-label="Copy reference"
                      />
                    </Tooltip>
                  </HStack>
                </Box>
              )}

              <Box bg="blue.50" p={3} borderRadius="md">
                <Text fontSize="xs" color="blue.800">
                  <strong>Note:</strong> Please transfer the exact amount to the
                  account details above. Your payment will be verified
                  automatically.
                </Text>
              </Box>

              <Button
                w="100%"
                bg={COLORS.green}
                color="white"
                _hover={{ bg: COLORS.primary }}
                onClick={() => {
                  setVirtualAccountModal(false);
                  onClose();
                  successNotifier(
                    "Please complete the transfer to the provided account"
                  );
                }}
                mt={1}
                size="sm"
              >
                Done
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  };

  return (
    <>
      <Button
        bg={btnBg}
        color={btnColor}
        onClick={onOpen}
        _hover={{ backgroundColor: `${btnBg}` }}
      >
        Pay Now
      </Button>
      <Modal
        size="sm"
        isOpen={isOpen}
        onClose={onClose}
        trapFocus={false}
        blockScrollOnMount={false}
      >
        <ModalOverlay />
        <ModalContent py="50px" bg={COLORS.white}>
          <ModalBody>
            <Heading fontSize="20px" color={COLORS.green} pb="30px">
              Choose Payment Method
            </Heading>

            {paymentChannel === "paystack" ? (
              <>
                <Text>
                  Kindly select your preferred payment method. You'll be
                  receiving an acknowledgement via Email/SMS.
                </Text>
                <Button
                  w={"100%"}
                  mt="50px"
                  bg={bgColor}
                  color={cardBg}
                  _hover={{ backgroundColor: `${bgColor}` }}
                  isLoading={isLoading}
                  onClick={payEstateDue}
                >
                  Pay with Paystack
                </Button>
                <Modal
                  isOpen={paymentModal}
                  onClose={onClose}
                  isCentered
                  bg="red"
                  size="sm"
                  trapFocus={false}
                >
                  <ModalOverlay />
                  <ModalContent bg={"white"}>
                    <ModalHeader>Payment checkout</ModalHeader>
                    <ModalCloseButton onClick={verifyPayments} />
                    <Stack pb="4" pt="2" pl="7%" fontWeight="500">
                      <HStack fontWeight={600} fontSize="12px">
                        <Text pr="2">
                          {dueTypes?.type}: &#8358;{" "}
                          {dueTypes?.amount?.toLocaleString()}
                        </Text>
                        <Text>
                          Paystack Charge: &#8358;{" "}
                          {transactionCharge?.toLocaleString()}{" "}
                        </Text>
                      </HStack>
                      {totalPendingAmount > 0 && (
                        <Text fontWeight={600} fontSize="12px">
                          Total Pending Amount: &#8358;{" "}
                          {totalPendingAmount?.toLocaleString()}
                        </Text>
                      )}
                      <Text fontWeight={600} fontSize="12px">
                        Total: &#8358;{" "}
                        {(
                          transactionCharge +
                          dueTypes?.amount +
                          (totalPendingAmount > 0 ? totalPendingAmount : 0)
                        )?.toLocaleString()}
                      </Text>
                    </Stack>
                    <ModalBody p="0">
                      <iframe
                        src={paymentData?.authorization_url}
                        width="100%"
                        height="450"
                        title="payment checkout"
                      ></iframe>
                    </ModalBody>
                  </ModalContent>
                </Modal>
              </>
            ) : paymentChannel === "routepay" ? (
              <Stack>
                <Text>
                  Kindly select your preferred payment method. You'll be
                  receiving an acknowledgement via Email/SMS.
                </Text>
                <Button
                  w={"100%"}
                  mt="50px"
                  bg={bgColor}
                  color={cardBg}
                  _hover={{ backgroundColor: `${bgColor}` }}
                  isLoading={paymentLoading}
                  isDisabled={paymentLoading}
                  onClick={handlePayment}
                >
                  {paymentLoading ? "Processing..." : "Pay with RoutePay"}
                </Button>
              </Stack>
            ) : paymentChannel === "virtual-account" ? (
              <Stack>
                <Text>
                  Kindly click the button below to generate your transfer
                  account details. You'll be receiving an acknowledgement via
                  Email/SMS once payment is confirmed.
                </Text>

                {/* Duration Selector - Year is always required, Month only for monthly frequency */}
                <Stack spacing={3} mt={4}>
                  {dueTypes?.frequency === "monthly" && (
                    <FormControl>
                      <FormLabel fontSize="14px" fontWeight="600">
                        Select Month
                      </FormLabel>
                      <Select
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        bg={COLORS.lightGrey}
                        focusBorderColor={COLORS.green}
                        placeholder="Select Month"
                      >
                        <option value="January">January</option>
                        <option value="February">February</option>
                        <option value="March">March</option>
                        <option value="April">April</option>
                        <option value="May">May</option>
                        <option value="June">June</option>
                        <option value="July">July</option>
                        <option value="August">August</option>
                        <option value="September">September</option>
                        <option value="October">October</option>
                        <option value="November">November</option>
                        <option value="December">December</option>
                      </Select>
                    </FormControl>
                  )}

                  <FormControl>
                    <FormLabel fontSize="14px" fontWeight="600">
                      Select Year
                    </FormLabel>
                    <Select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                      bg={COLORS.lightGrey}
                      focusBorderColor={COLORS.green}
                      placeholder="Select Year"
                    >
                      {Array.from({ length: 10 }, (_, i) => {
                        const year = new Date().getFullYear() - i;
                        return (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Stack>

                {/* Amount Breakdown */}
                <VStack spacing={3} mt={4}>
                  <HStack
                    fontWeight={600}
                    fontSize="14px"
                    w="100%"
                    justify="space-between"
                  >
                    <Text>{dueTypes?.type}:</Text>
                    <Text>&#8358; {dueTypes?.amount?.toLocaleString()}</Text>
                  </HStack>
                  <HStack
                    fontWeight={600}
                    fontSize="14px"
                    w="100%"
                    justify="space-between"
                  >
                    <Text>Transaction Charge:</Text>
                    <Text>&#8358; {transactionCharge?.toLocaleString()}</Text>
                  </HStack>
                  {totalPendingAmount > 0 && (
                    <HStack
                      fontWeight={600}
                      fontSize="14px"
                      w="100%"
                      justify="space-between"
                    >
                      <Text>Pending Amount:</Text>
                      <Text>
                        &#8358; {totalPendingAmount?.toLocaleString()}
                      </Text>
                    </HStack>
                  )}
                  <Divider />
                  <HStack
                    fontWeight={700}
                    fontSize="16px"
                    w="100%"
                    justify="space-between"
                    color={COLORS.green}
                  >
                    <Text>Total:</Text>
                    <Text>
                      &#8358;{" "}
                      {(
                        transactionCharge +
                        dueTypes?.amount +
                        (totalPendingAmount > 0 ? totalPendingAmount : 0)
                      )?.toLocaleString()}
                    </Text>
                  </HStack>
                </VStack>

                <Button
                  w={"100%"}
                  mt="30px"
                  bg={bgColor}
                  color={cardBg}
                  _hover={{ backgroundColor: `${bgColor}` }}
                  isLoading={virtualAccountLoading}
                  isDisabled={
                    virtualAccountLoading ||
                    (dueTypes?.frequency === "monthly"
                      ? !selectedMonth || !selectedYear
                      : !selectedYear)
                  }
                  onClick={handleVirtualAccountPayment}
                >
                  {virtualAccountLoading
                    ? "Generating..."
                    : "Pay with Transfer"}
                </Button>
              </Stack>
            ) : (
              <Stack>
                <Text fontSize={"1rem"}>
                  Kindly select your preferred payment method.
                </Text>
                <Text mb="4" fontWeight="bold">
                  Note: if you want to pay with card, use RoutePay, but if you
                  want to pay with transfer, use Paystack.
                </Text>
                <Button
                  w={"100%"}
                  mt="50px"
                  bg={"#f85a0d"}
                  color={COLORS.white}
                  _hover={{ backgroundColor: `${bgColor}` }}
                  isLoading={paymentLoading}
                  isDisabled={paymentLoading}
                  onClick={handlePayment}
                >
                  {paymentLoading ? "Processing..." : "Pay with RoutePay"}
                </Button>

                <>
                  <Button
                    w={"100%"}
                    mt="50px"
                    bg={"#0aa5db"}
                    color={COLORS.white}
                    _hover={{ backgroundColor: `${bgColor}` }}
                    isLoading={isLoading}
                    onClick={payEstateDue}
                  >
                    Pay with Paystack
                  </Button>
                  <Modal
                    isOpen={paymentModal}
                    onClose={onClose}
                    isCentered
                    bg="red"
                    size="sm"
                    trapFocus={false}
                  >
                    <ModalOverlay />
                    <ModalContent bg={"white"}>
                      <ModalHeader>Payment checkout</ModalHeader>
                      <ModalCloseButton onClick={verifyPayments} />
                      <Stack pb="4" pt="2" pl="7%" fontWeight="500">
                        <HStack fontWeight={600} fontSize="12px">
                          <Text pr="2">
                            {dueTypes?.type}: &#8358;{" "}
                            {dueTypes?.amount?.toLocaleString()}
                          </Text>
                          <Text>
                            Paystack Charge: &#8358;{" "}
                            {transactionCharge?.toLocaleString()}{" "}
                          </Text>
                        </HStack>
                        {totalPendingAmount > 0 && (
                          <Text fontWeight={600} fontSize="12px">
                            Total Pending Amount: &#8358;{" "}
                            {totalPendingAmount?.toLocaleString()}
                          </Text>
                        )}
                        <Text fontWeight={600} fontSize="12px">
                          Total: &#8358;{" "}
                          {(
                            transactionCharge +
                            dueTypes?.amount +
                            (totalPendingAmount > 0 ? totalPendingAmount : 0)
                          )?.toLocaleString()}
                        </Text>
                      </Stack>
                      <ModalBody p="0">
                        <iframe
                          src={paymentData?.authorization_url}
                          width="100%"
                          height="450"
                          title="payment checkout"
                        ></iframe>
                      </ModalBody>
                    </ModalContent>
                  </Modal>
                </>
              </Stack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Virtual Account Details Modal */}
      {virtualAccountData && <VirtualAccountDetailsModal />}
    </>
  );
};
